import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { entityLink } from "../lib/drill";
import DataTable from "../components/DataTable";
import { ExternalLink } from "lucide-react";

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/audit").then((r) => setLogs(r.data));
  }, []);

  const modules = useMemo(() => [...new Set(logs.map((l) => l.entity_type).filter(Boolean))], [logs]);
  const actions = useMemo(() => [...new Set(logs.map((l) => l.action).filter(Boolean))], [logs]);
  const actors = useMemo(() => [...new Set(logs.map((l) => l.actor_name).filter(Boolean))], [logs]);

  return (
    <div data-testid="audit-page">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold">Governance</div>
      <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Audit Log</h1>

      <div className="mt-5">
        <DataTable
          testId="audit-table"
          rows={logs}
          pageSize={15}
          searchKeys={["details", "actor_name", "action", "entity_type"]}
          filters={[
            { key: "entity_type", label: "Module", options: modules.map((m) => ({ value: m, label: m.replace(/_/g, " ") })) },
            { key: "action", label: "Action", options: actions.map((a) => ({ value: a, label: a })) },
            { key: "actor_name", label: "Actor", options: actors.map((a) => ({ value: a, label: a })) },
          ]}
          columns={[
            { key: "timestamp", label: "Time", sortable: true, render: (l) => (
              <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>
            ) },
            { key: "actor_name", label: "Actor", sortable: true, render: (l) => <span className="font-semibold whitespace-nowrap">{l.actor_name}</span> },
            { key: "actor_role", label: "Role", sortable: true, render: (l) => (
              <span className="text-xs capitalize text-muted-foreground whitespace-nowrap">{l.actor_role?.replace(/_/g, " ")}</span>
            ) },
            { key: "action", label: "Action", sortable: true, render: (l) => (
              <span className="text-[10px] uppercase tracking-wider bg-secondary px-2 py-1 rounded-md font-bold">{l.action}</span>
            ) },
            { key: "entity_type", label: "Module", sortable: true, render: (l) => <span className="text-xs capitalize">{l.entity_type?.replace(/_/g, " ")}</span> },
            { key: "details", label: "Details", render: (l) => {
              const to = entityLink(l.entity_type, l.entity_id);
              if (!to) return l.details;
              return (
                <button data-testid={`audit-drill-${l.id}`} onClick={() => navigate(to)}
                  className="text-left inline-flex items-start gap-1.5 hover:text-[#E05A47] transition-colors duration-100 group">
                  <span>{l.details}</span>
                  <ExternalLink size={12} className="mt-0.5 shrink-0 opacity-30 group-hover:opacity-100" />
                </button>
              );
            } },
          ]}
        />
      </div>
    </div>
  );
}
